import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../../supabase";
import { useAuth } from "../../contexts/AuthContext";

const AdminDashboard = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all"); // all, verified, unverified
    const [updatingUsers, setUpdatingUsers] = useState(new Set()); // Track which users are being updated
    const [receiveVerificationEmails, setReceiveVerificationEmails] = useState(false);
    const [updatingEmailPreference, setUpdatingEmailPreference] = useState(false);
    const [targetUserId, setTargetUserId] = useState(null);

    useEffect(() => {
        // Read candidateId from query params so email links can deep-link to a specific candidate
        const searchParams = new URLSearchParams(location.search);
        const candidateId = searchParams.get("candidateId");
        if (candidateId) {
            setTargetUserId(candidateId);
        }

        fetchUsers();
        fetchEmailPreference();
    }, [user, location.search]);

    const fetchEmailPreference = async () => {
        if (!user) return;
        
        try {
            // First check user_metadata
            const metadataPreference = user.user_metadata?.receive_verification_emails;
            
            // Also check users_profiles table (if column exists)
            const { data: profile, error } = await supabase
                .from('users_profiles')
                .select('receive_verification_emails')
                .eq('profileid', user.id)
                .maybeSingle();
            
            // Use profile preference if available, otherwise use metadata, default to false
            const preference = profile?.receive_verification_emails !== undefined 
                ? profile.receive_verification_emails 
                : (metadataPreference === true); // Default to false (only true if explicitly set)
            
            setReceiveVerificationEmails(preference);
        } catch (error) {
            console.error('Error fetching email preference:', error);
            // Default to false on error
            setReceiveVerificationEmails(false);
        }
    };

    const handleToggleEmailPreference = async () => {
        if (!user) return;
        
        setUpdatingEmailPreference(true);
        try {
            const newPreference = !receiveVerificationEmails;
            
            // Update user_metadata (always works)
            const { error: authError } = await supabase.auth.updateUser({
                data: {
                    receive_verification_emails: newPreference
                }
            });
            
            if (authError) throw authError;
            
            // Also update users_profiles table if column exists
            const { error: profileError } = await supabase
                .from('users_profiles')
                .update({ receive_verification_emails: newPreference })
                .eq('profileid', user.id);
            
            // If column doesn't exist, that's okay - we'll use metadata
            if (profileError && profileError.code !== '42703') {
                console.warn('Could not update users_profiles preference:', profileError);
            }
            
            setReceiveVerificationEmails(newPreference);
        } catch (error) {
            console.error('Failed to update email preference:', error);
            alert('Failed to update email preference. Please try again.');
        } finally {
            setUpdatingEmailPreference(false);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            
            // Approach: Fetch users with HPCSA from users_profiles
            // Note: This requires adding hpcsa_reg_no and hpcsa_verified columns to users_profiles table
            // OR using a Supabase RPC function to get this data from auth.users
            
            // Try to fetch with HPCSA columns (if they exist)
            let { data: profilesWithHPCSA, error: profilesError } = await supabase
                .from('users_profiles')
                .select('profileid, email, firstname, lastname, hpcsa_reg_no, hpcsa_verified')
                .eq('role', 'Employee')
                .not('hpcsa_reg_no', 'is', null)
                .order('firstname', { ascending: true });

            // If columns don't exist, try alternative: use RPC function
            if (profilesError && profilesError.code === '42703') {
                // Column doesn't exist - try RPC function
                const { data: rpcData, error: rpcError } = await supabase
                    .rpc('get_users_with_hpcsa');
                
                if (rpcError) {
                    console.error('RPC error:', rpcError);
                    // Fallback: fetch all employees and try to get HPCSA from metadata sync
                    // This would require a database trigger or manual sync
                    const { data: allProfiles } = await supabase
                        .from('users_profiles')
                        .select('profileid, email, firstname, lastname')
                        .eq('role', 'Employee')
                        .order('firstname', { ascending: true });
                    
                    // Note: Without hpcsa columns or RPC, we can't get HPCSA data
                    // The database needs to be set up with one of these options
                    setUsers([]);
                    alert('HPCSA data not available. Please set up database columns or RPC function.');
                    return;
                }
                
                profilesWithHPCSA = rpcData;
            } else if (profilesError) {
                throw profilesError;
            }

            const formattedUsers = (profilesWithHPCSA || []).map(profile => {
                return {
                    id: profile.profileid,
                    email: profile.email,
                    firstName: profile.firstname || '',
                    lastName: profile.lastname || '',
                    hpcsaRegNo: profile.hpcsa_reg_no || '',
                    // Ensure boolean conversion - handle null, undefined, string "true"/"false", etc.
                    verified: profile.hpcsa_verified === true || profile.hpcsa_verified === 'true' || profile.hpcsa_verified === 1,
                    fullName: `${profile.firstname || ''} ${profile.lastname || ''}`.trim() || profile.email
                };
            });

            setUsers(formattedUsers);
        } catch (error) {
            console.error("Error fetching users:", error);
            alert('Error fetching users. Please check console for details.');
        } finally {
            setLoading(false);
        }
    };

    // When we have both a target user id from the URL and the loaded users,
    // focus the list on that candidate so the admin can review them immediately.
    useEffect(() => {
        if (!targetUserId || users.length === 0) return;

        const targetUser = users.find((u) => u.id === targetUserId);
        if (targetUser) {
            // Narrow the list down to the target candidate using their email/name/HPCSA number
            setSearchTerm(
                targetUser.email ||
                targetUser.fullName ||
                targetUser.hpcsaRegNo ||
                ""
            );

            // Scroll the row into view if it exists
            const rowEl = document.getElementById(`user-row-${targetUser.id}`);
            if (rowEl) {
                rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }, [targetUserId, users]);

    const handleVerify = async (userId, currentStatus) => {
        // Prevent double-clicks
        if (updatingUsers.has(userId)) return;
        
        try {
            setUpdatingUsers(prev => new Set(prev).add(userId));
            const newStatus = !currentStatus;
            
            // First, check if the user exists and get their current data
            const { data: existingUser, error: checkError } = await supabase
                .from('users_profiles')
                .select('profileid, hpcsa_verified, email, firstname, lastname')
                .eq('profileid', userId)
                .maybeSingle();

            if (checkError && checkError.code !== 'PGRST116') {
                console.error('Error checking user:', checkError);
                alert(`Error checking user: ${checkError.message}`);
                setUpdatingUsers(prev => {
                    const next = new Set(prev);
                    next.delete(userId);
                    return next;
                });
                return;
            }

            if (!existingUser) {
                alert(`User not found in database. User ID: ${userId}. Please refresh and try again.`);
                setUpdatingUsers(prev => {
                    const next = new Set(prev);
                    next.delete(userId);
                    return next;
                });
                return;
            }

            // Check if user has HPCSA registration number
            const userHasRegNo = existingUser.hpcsa_reg_no && existingUser.hpcsa_reg_no.trim().length > 0;
            
            // Warn if trying to verify without reg number
            if (newStatus === true && !userHasRegNo) {
                const confirmMessage = `Warning: This user (${existingUser.email || existingUser.firstname || 'User'}) does not have an HPCSA registration number.\n\n` +
                    `The blue verification tick will NOT display without a registration number.\n\n` +
                    `Do you still want to mark them as verified?`;
                
                if (!window.confirm(confirmMessage)) {
                    setUpdatingUsers(prev => {
                        const next = new Set(prev);
                        next.delete(userId);
                        return next;
                    });
                    return;
                }
            }

            // Update verification status in users_profiles table
            // Use .select() to get the updated row back and verify it worked
            const { data: updatedRows, error: profileError, count } = await supabase
                .from('users_profiles')
                .update({ hpcsa_verified: newStatus })
                .eq('profileid', userId)
                .select('profileid, hpcsa_verified, hpcsa_reg_no');

            if (profileError) {
                
                // If column doesn't exist, show helpful error
                if (profileError.code === '42703' || profileError.message?.includes('column') || profileError.message?.includes('hpcsa_verified')) {
                    alert(`Database column 'hpcsa_verified' does not exist. Please add this column to the users_profiles table:\n\nALTER TABLE users_profiles ADD COLUMN hpcsa_verified BOOLEAN DEFAULT FALSE;`);
                    setUpdatingUsers(prev => {
                        const next = new Set(prev);
                        next.delete(userId);
                        return next;
                    });
                    return;
                } else {
                    alert(`Failed to update verification status: ${profileError.message}`);
                    setUpdatingUsers(prev => {
                        const next = new Set(prev);
                        next.delete(userId);
                        return next;
                    });
                    return;
                }
            }

            // Check if any rows were updated
            if (!updatedRows || updatedRows.length === 0) {
                // Check if user has HPCSA reg number
                const userHasRegNoForError = existingUser?.hpcsa_reg_no && existingUser.hpcsa_reg_no.trim().length > 0;
                
                alert(
                    `Update failed for user ${existingUser?.email || userId}.\n\n` +
                    `Possible causes:\n` +
                    `1. Row Level Security (RLS) policy is blocking the update\n` +
                    `2. User profile doesn't exist or has permission issues\n` +
                    `${userHasRegNoForError ? '' : '3. User does not have an HPCSA registration number\n'}` +
                    `\nCheck the browser console for detailed error information.\n\n` +
                    `If RLS is the issue, update the policy with:\n\n` +
                    'DROP POLICY IF EXISTS "Allow admins to update HPCSA verification" ON users_profiles;\n\n' +
                    'CREATE POLICY "Allow admins to update HPCSA verification"\n' +
                    'ON users_profiles\n' +
                    'FOR UPDATE\n' +
                    'USING (auth.jwt() ->> \'email\' = ANY(ARRAY[\'nizaam@fourminutemedicine.com\', \'kylie@fourminutemedicine.com\', \'vela@fourminutemedicine.com\']))\n' +
                    'WITH CHECK (auth.jwt() ->> \'email\' = ANY(ARRAY[\'nizaam@fourminutemedicine.com\', \'kylie@fourminutemedicine.com\', \'vela@fourminutemedicine.com\']));'
                );
                setUpdatingUsers(prev => {
                    const next = new Set(prev);
                    next.delete(userId);
                    return next;
                });
                return;
            }

            // Check the returned data to verify the update
            const updatedRow = updatedRows[0];
            const hasRegNo = updatedRow.hpcsa_reg_no && updatedRow.hpcsa_reg_no.trim().length > 0;
            
            // Handle different boolean representations (true/false, 'true'/'false', 1/0, 't'/'f', null)
            const actualValue = updatedRow.hpcsa_verified;
            const isVerified = actualValue === true || actualValue === 'true' || actualValue === 1 || actualValue === '1' || actualValue === 't' || actualValue === 'T';
            const expectedVerified = newStatus === true;
            
            if (isVerified !== expectedVerified) {
                alert(`Update may not have saved correctly. Expected: ${expectedVerified}, Got: ${actualValue}. Please check database permissions and try again.`);
                setUpdatingUsers(prev => {
                    const next = new Set(prev);
                    next.delete(userId);
                    return next;
                });
                return;
            }

            // Double-check by fetching the record again after a small delay
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const { data: verifyData, error: verifyError } = await supabase
                .from('users_profiles')
                .select('hpcsa_verified')
                .eq('profileid', userId)
                .maybeSingle();

            if (verifyError) {
                // Don't fail here, we already verified from the update response
            } else if (verifyData) {
                const verifyValue = verifyData.hpcsa_verified === true || verifyData.hpcsa_verified === 'true' || verifyData.hpcsa_verified === 1 || verifyData.hpcsa_verified === '1' || verifyData.hpcsa_verified === 't' || verifyData.hpcsa_verified === 'T';
                if (verifyValue !== expectedVerified) {
                    alert('Update completed but verification check failed. Please refresh to confirm.');
                    setUpdatingUsers(prev => {
                        const next = new Set(prev);
                        next.delete(userId);
                        return next;
                    });
                    return;
                }
            }

            // Update local state
            setUsers(users.map(u => 
                u.id === userId 
                    ? { ...u, verified: newStatus }
                    : u
            ));

            // Clear the profile cache for this user so their profile shows updated status
            try {
                const cacheKey = `profile_${userId}`;
                sessionStorage.removeItem(cacheKey);
                
                // Dispatch a custom event to notify ProfileInfoContext to refresh
                // This works for same-tab scenarios
                window.dispatchEvent(new CustomEvent('profileCacheCleared', { 
                    detail: { userId } 
                }));
            } catch (cacheError) {
                // Silent fail for cache clearing
            }
        } catch (error) {
            alert(`Failed to update verification status: ${error.message || 'Unknown error'}`);
        } finally {
            setUpdatingUsers(prev => {
                const next = new Set(prev);
                next.delete(userId);
                return next;
            });
        }
    };

    // Filter users based on search and status
    const filteredUsers = users.filter(user => {
        const matchesSearch = 
            user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.hpcsaRegNo.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = 
            filterStatus === 'all' ||
            (filterStatus === 'verified' && user.verified) ||
            (filterStatus === 'unverified' && !user.verified);
        
        return matchesSearch && matchesStatus;
    });

    // Calculate statistics
    const totalUsers = users.length;
    const verifiedCount = users.filter(u => u.verified).length;
    const unverifiedCount = users.filter(u => !u.verified).length;
    const verificationRate = totalUsers > 0 ? Math.round((verifiedCount / totalUsers) * 100) : 0;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading verification data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-4 md:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900 mb-2">HPCSA Verification</h1>
                            <p className="text-gray-600 text-lg">Manage and verify employee HPCSA registration numbers</p>
                        </div>
                        <div className="hidden md:flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-sm text-gray-600">Admin Dashboard</span>
                        </div>
                    </div>
                </div>

                {/* Settings Card */}
                <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border-l-4 border-indigo-500">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">Email Notifications</h3>
                            <p className="text-sm text-gray-600">Receive email notifications when new candidates need HPCSA verification</p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={receiveVerificationEmails}
                                    onChange={handleToggleEmailPreference}
                                    disabled={updatingEmailPreference}
                                    className="sr-only peer"
                                />
                                <div className={`
                                    relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out
                                    ${receiveVerificationEmails 
                                        ? 'bg-green-500' 
                                        : 'bg-gray-300'
                                    }
                                    ${updatingEmailPreference ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                    peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300
                                `}>
                                    <div className={`
                                        absolute top-0.5 left-0.5 bg-white rounded-full h-5 w-5 transition-transform duration-200 ease-in-out
                                        ${receiveVerificationEmails ? 'translate-x-5' : 'translate-x-0'}
                                        ${updatingEmailPreference ? 'opacity-50' : ''}
                                    `}>
                                        {updatingEmailPreference && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </label>
                            <span className="text-sm font-medium text-gray-700 min-w-[80px]">
                                {receiveVerificationEmails ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Users</p>
                                <p className="text-3xl font-bold text-gray-900 mt-1">{totalUsers}</p>
                            </div>
                            <div className="bg-blue-100 rounded-full p-3">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            </div>
                        </div>

                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Verified</p>
                                <p className="text-3xl font-bold text-green-600 mt-1">{verifiedCount}</p>
                            </div>
                            <div className="bg-green-100 rounded-full p-3">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>

                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Pending</p>
                                <p className="text-3xl font-bold text-orange-600 mt-1">{unverifiedCount}</p>
                            </div>
                            <div className="bg-orange-100 rounded-full p-3">
                                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Verification Rate</p>
                                <p className="text-3xl font-bold text-purple-600 mt-1">{verificationRate}%</p>
                            </div>
                            <div className="bg-purple-100 rounded-full p-3">
                                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filter Bar */}
                <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Search by name, email, or HPCSA number..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                </svg>
                            </div>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="pl-10 pr-8 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white cursor-pointer transition-all"
                            >
                                <option value="all">All Status</option>
                                <option value="verified">Verified Only</option>
                                <option value="unverified">Pending Only</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                        <h2 className="text-lg font-semibold text-gray-900">Verification List</h2>
                        <p className="text-sm text-gray-500 mt-1">Showing {filteredUsers.length} of {totalUsers} users</p>
                    </div>
                    <div className="overflow-x-auto">
                        {filteredUsers.length === 0 ? (
                            <div className="text-center py-16">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {searchTerm || filterStatus !== 'all' 
                                        ? 'Try adjusting your search or filter criteria'
                                        : 'No users with HPCSA numbers found'}
                                </p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                            HPCSA Number
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                            Action
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredUsers.map((user, index) => (
                                        <tr
                                            key={user.id}
                                            id={`user-row-${user.id}`}
                                            className="hover:bg-blue-50 transition-colors duration-150"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center space-x-3">
                                                    <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                                        {user.fullName.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="flex items-center">
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {user.fullName}
                                                        </span>
                                                        <img
                                                            src="/SealCheck.svg"
                                                            alt={user.verified ? "Verified" : "Unverified"}
                                                            className={`ml-3 w-6 h-6 transition-all ${
                                                                user.verified 
                                                                    ? 'opacity-100' 
                                                                    : 'opacity-30 grayscale'
                                                            }`}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-600">{user.email}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-mono font-semibold text-gray-900 bg-gray-50 px-3 py-1 rounded-md inline-block">
                                                    {user.hpcsaRegNo}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={user.verified}
                                                        onChange={() => handleVerify(user.id, user.verified)}
                                                        disabled={updatingUsers.has(user.id)}
                                                        className="sr-only peer"
                                                    />
                                                    <div className={`
                                                        relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out
                                                        ${user.verified 
                                                            ? 'bg-green-500' 
                                                            : 'bg-gray-300'
                                                        }
                                                        ${updatingUsers.has(user.id) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                                        peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300
                                                    `}>
                                                        <div className={`
                                                            absolute top-0.5 left-0.5 bg-white rounded-full h-5 w-5 transition-transform duration-200 ease-in-out
                                                            ${user.verified ? 'translate-x-5' : 'translate-x-0'}
                                                            ${updatingUsers.has(user.id) ? 'opacity-50' : ''}
                                                        `}>
                                                            {updatingUsers.has(user.id) && (
                                                                <div className="absolute inset-0 flex items-center justify-center">
                                                                    <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <span className="ml-3 text-sm font-medium text-gray-700">
                                                        {user.verified ? 'Verified' : 'Pending'}
                                                    </span>
                                                </label>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                                    user.verified
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-orange-100 text-orange-800'
                                                }`}>
                                                    {user.verified ? (
                                                        <>
                                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                            Verified
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                            </svg>
                                                            Pending
                                                        </>
                                                    )}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;

